import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase, NewsPost, I18nText } from '../../../lib/supabase';
import Layout from '../../../components/feature/Layout';

type Lang = 'ko' | 'en' | 'vi';

const DEFAULT_OG_IMAGE =
  'https://onstllduqumahkhkcxew.supabase.co/storage/v1/object/public/og-images/og_1774156004355.png';

const getI18n = (obj: I18nText | undefined, lang: Lang): string => {
  if (!obj) return '';
  return obj[lang] || obj.ko || obj.en || obj.vi || '';
};

const formatDate = (dateStr: string | null, lang: Lang): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const locale = lang === 'ko' ? 'ko-KR' : lang === 'vi' ? 'vi-VN' : 'en-US';
  return d.toLocaleDateString(locale, opts);
};

/** HTML 태그 제거 → 순수 텍스트 추출 */
const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** meta 태그를 동적으로 upsert */
const setMetaTag = (attr: string, attrValue: string, content: string) => {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) as Lang) || 'ko';
  const [post, setPost] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('news_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      setPost(data as NewsPost | null);
      setLoading(false);
    };
    if (slug) fetchPost();
  }, [slug]);

  /* ── OG / Twitter Card 동적 업데이트 ── */
  useEffect(() => {
    if (!post) return;

    const title = getI18n(post.title_i18n, lang) || 'MONARU 소식';
    const rawContent = getI18n(post.content_i18n, lang);
    const description = stripHtml(rawContent).slice(0, 155) || 'MONARU 최신 소식을 확인하세요.';
    const image = post.cover_image_url || DEFAULT_OG_IMAGE;
    const url = window.location.href;
    const ogTitle = `${title} | MONARU`;

    // <title>
    document.title = ogTitle;

    // Open Graph
    setMetaTag('property', 'og:title', ogTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:image', image);
    setMetaTag('property', 'og:url', url);
    setMetaTag('property', 'og:type', 'article');
    setMetaTag('property', 'og:site_name', 'MONARU');

    // Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', ogTitle);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', image);

    // 페이지 이탈 시 기본값 복원
    return () => {
      document.title = '모나루 (MONARU) 브랜드 무역 파트너';
      setMetaTag('property', 'og:title', '모나루 (MONARU) 브랜드 무역 파트너');
      setMetaTag('property', 'og:description', 'MONARU is the premier Korea-Vietnam bilateral brand trade agency.');
      setMetaTag('property', 'og:image', DEFAULT_OG_IMAGE);
      setMetaTag('property', 'og:url', 'https://monaru.co.kr/');
      setMetaTag('property', 'og:type', 'website');
      setMetaTag('name', 'twitter:title', '모나루 (MONARU) 브랜드 무역 파트너');
      setMetaTag('name', 'twitter:description', 'MONARU is the premier Korea-Vietnam bilateral brand trade agency.');
      setMetaTag('name', 'twitter:image', DEFAULT_OG_IMAGE);
    };
  }, [post, lang]);

  const handleShare = () => {
    // Edge Function OG 프록시 URL (크롤러용) vs 실제 페이지 URL
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-navy/20 border-t-navy rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#F8F9FB] flex flex-col items-center justify-center gap-4 pt-24">
          <i className="ri-file-unknow-line text-5xl text-navy/20" />
          <p className="text-navy/50 font-sans text-sm">{t('newsPage.notFound')}</p>
          <Link
            to="/news"
            onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
            className="mt-2 px-5 py-2.5 bg-navy text-white text-sm font-sans rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('newsPage.backToList')}
          </Link>
        </div>
      </Layout>
    );
  }

  const title = getI18n(post.title_i18n, lang);
  const content = getI18n(post.content_i18n, lang);
  const dateStr = formatDate(post.published_at, lang);
  const ogDesc = stripHtml(content).slice(0, 155) || 'MONARU 최신 소식을 확인하세요.';
  const ogImage = post.cover_image_url || DEFAULT_OG_IMAGE;
  const ogTitle = `${title} | MONARU`;
  const ogUrl = `https://monaru.co.kr/news/${post.slug}`;

  return (
    <Layout>
      {/* React 19 native meta hoisting — JS 실행하는 크롤러에 대한 보조 레이어 */}
      <title>{ogTitle}</title>
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="MONARU" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDesc} />
      <meta name="twitter:image" content={ogImage} />
      <link rel="canonical" href={ogUrl} />

      {/* Cover */}
      <div className="w-full h-72 lg:h-[420px] relative overflow-hidden">
        {post.cover_image_url ? (
          <img
            src={post.cover_image_url}
            alt={title}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-navy/20 to-navy/5 flex items-center justify-center">
            <i className="ri-newspaper-line text-6xl text-navy/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-navy/30" />
      </div>

      {/* Content */}
      <section className="bg-[#F8F9FB] sm:pb-20">
        <div className="max-w-3xl mx-auto px-0 sm:px-6 -mt-20 relative z-10">
          <article className="bg-white sm:rounded-2xl border-0 sm:border sm:border-gray-100 px-5 py-7 sm:p-8 lg:p-12">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-navy/5 text-navy/60 text-xs font-sans font-semibold rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="font-serif text-navy text-2xl lg:text-3xl font-bold leading-snug mb-5">
              {title}
            </h1>

            {/* Meta */}
            <div className="flex items-center gap-4 pb-6 border-b border-gray-100 mb-8 flex-wrap">
              {post.author && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-navy/10 flex items-center justify-center">
                    <i className="ri-user-line text-navy/50 text-xs" />
                  </div>
                  <span className="text-sm font-sans text-navy/60">
                    {t('newsPage.by')} <strong className="text-navy/80">{post.author}</strong>
                  </span>
                </div>
              )}
              {dateStr && (
                <div className="flex items-center gap-1.5 text-navy/40">
                  <i className="ri-calendar-line text-sm" />
                  <span className="text-xs font-sans">{dateStr}</span>
                </div>
              )}
            </div>

            {/* Body */}
            {content ? (
              <div
                className="prose prose-sm max-w-none font-sans text-navy/75 leading-relaxed
                  prose-headings:font-serif prose-headings:text-navy
                  prose-h2:text-xl prose-h3:text-lg
                  prose-strong:text-navy prose-strong:font-semibold
                  prose-a:text-navy prose-a:underline
                  prose-li:text-navy/70
                  prose-img:rounded-xl prose-img:w-full
                  prose-blockquote:border-l-navy/30 prose-blockquote:text-navy/60"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <p className="text-navy/30 font-sans text-sm italic">콘텐츠가 없습니다.</p>
            )}

            {/* Actions */}
            <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4">
              <Link
                to="/news"
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className="flex items-center gap-2 text-sm font-sans text-navy/60 hover:text-navy transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                {t('newsPage.backToList')}
              </Link>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-sans text-navy/60 hover:text-navy hover:border-navy transition-all cursor-pointer whitespace-nowrap"
              >
                <i className={copied ? 'ri-check-line text-green-500' : 'ri-share-line'} />
                {copied ? '링크 복사됨!' : t('newsPage.share')}
              </button>
            </div>
          </article>
        </div>
      </section>
    </Layout>
  );
}
